import { ButtonGroup, Checkbox, FormControlLabel, Grid } from '@material-ui/core';
import { Button, Card, Icons, MaterialPalette, Typography } from '@nutrien/cxp-components';
import React, { useEffect, useState } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import { Subscription } from 'rxjs';

import { FileCategory, ISignAndStampData } from '../../models/models';
import { useSignAndStampFile } from '../../rxdb/signAndStamp/useSignAndStampFile';
import type { SignAndStampUserLevelDocument } from '../../rxdb/signAndStampUserLevel/signAndStampUserLevel.types';
import { useSignAndStampUserLevel } from '../../rxdb/signAndStampUserLevel/useSignAndStampUserLevel';
import { isJestTest } from '../../test-helpers/isJestTest';
import { sentryCaptureException } from '../../utilities/sentryCaptureException';
import { useFileStorage } from '../../utilities/useFileStorage';
import { useNotification } from '../../utilities/useNotification';
import {
  ALLOW_EXTENSIONS,
  FALSE,
  INVALID_FILE,
  INVALID_FILE_TYPE,
  SPECIAL_CHARACTER_ERROR,
  TRUE,
} from '../../utilities/constants';
import {
  checkFileCorruption,
  getLocalStorageCurrentUserId,
  hasSpecialCharacter,
} from '../../utilities/utilityFunctions';
import SignatureAndStampStyles from './SignatureAndStamp.styles';
import SignatureAndStampView from './SignatureAndStampView';
import UserConsent from './UserConsent';

interface Props {
  setIsChecked: (val: boolean) => void;
  isChecked: boolean;
  setIsUploadButDisable: (val: boolean) => void;
  isHandleSignAndStamp: boolean;
  setSaveSignAndStamp: (val: boolean) => void;
}

const SignatureAndStampUpload: React.FC<Props> = ({
  setIsChecked,
  isChecked,
  setIsUploadButDisable,
  isHandleSignAndStamp,
  setSaveSignAndStamp,
}) => {
  const classes = SignatureAndStampStyles();
  const signatureCanvasHeight = '500';
  let signatureCanvas: SignatureCanvas | null;
  const { uploadSignAndStamp, getAttachmentFromIndexDB } = useFileStorage();
  const localStorageCurrentUserId = getLocalStorageCurrentUserId();
  const [openUserConsent, setOpenUserConsent] = useState<boolean>(false);
  const [openAlert, setIsOpenAlert] = useState<boolean>(false);
  const [isAccepted, setIsAccepted] = useState<boolean>(true);
  const [isSignature, setIsSignature] = useState<boolean>(false);
  const [uploadContainer, setUploadContainer] = useState<boolean>(true);
  const [signContainer, setSignContainer] = useState<boolean>(false);
  const [selectedFiles, setSelectedFiles] = useState<ISignAndStampData[]>([]);
  const [userSignData, setUserSignData] = useState<ISignAndStampData>();
  const [userStampData, setUserStampData] = useState<ISignAndStampData>();
  const [signData, setSignData] = useState<ISignAndStampData>();
  const [stampData, setStampData] = useState<ISignAndStampData>();
  const [deleteUserFiles, setDeleteUserFiles] = useState<ISignAndStampData[]>([]);
  const { successNotification, errorNotification } = useNotification();
  const { deleteSignAndStampFile } = useSignAndStampFile();

  const label = (
    <Grid item container>
      <Typography variant="body1" className={classes.content}>
        Please review
      </Typography>
      <Typography
        color="primary"
        className={classes.spanMsg}
        onClick={() => setOpenUserConsent(true)}
        data-testid="userconsent"
      >
        User Consent
      </Typography>
      <Typography variant="body1" className={classes.content}>
        for saving Signature and Stamp Image
      </Typography>
    </Grid>
  );

  const {
    signAndStampUserLevelInitialized,
    signAndStampUserLevelCollection,
    getSignAndStampUserLevel,
  } = useSignAndStampUserLevel();

  const [signAndStampUserLevelUpdateSubscription, setSignAndStampUserLevelUpdateSubscription] =
    useState<Subscription>();
  const getSignAndStampUserLevelFromRxDB = async () => {
    if (signAndStampUserLevelCollection) {
      const signAndStampUserLevelRxDocument: SignAndStampUserLevelDocument[] =
        await getSignAndStampUserLevel();
      if (signAndStampUserLevelRxDocument) {
        const signUserData = signAndStampUserLevelRxDocument.find(
          (item: SignAndStampUserLevelDocument) =>
            item.fileCategory === FileCategory.Sign &&
            item.userId === localStorageCurrentUserId &&
            item.isSaveForFuture === true &&
            item.isDelete === FALSE,
        );
        if (signUserData) {
          const data: ISignAndStampData = {
            fileUUID: signUserData.fileUUID,
            updatedName: signUserData.name,
            category: signUserData.fileCategory,
            filePath: signUserData.originalPath,
          };
          setSignData(undefined);
          setUserSignData(data);
        }

        const stampUserData = signAndStampUserLevelRxDocument.find(
          (item: SignAndStampUserLevelDocument) =>
            item.fileCategory === FileCategory.Stamp &&
            item.userId === localStorageCurrentUserId &&
            item.isSaveForFuture === true &&
            item.isDelete === FALSE,
        );
        if (stampUserData) {
          const data: ISignAndStampData = {
            fileUUID: stampUserData.fileUUID,
            updatedName: stampUserData.name,
            category: stampUserData.fileCategory,
            filePath: stampUserData.originalPath,
          };
          setStampData(undefined);
          setUserStampData(data);
        }
      }
    }
  };

  const subscribeToSignAndStampUserLevel = async () => {
    if (signAndStampUserLevelInitialized && signAndStampUserLevelCollection) {
      const signAndStampUserLevelUpdateSub = signAndStampUserLevelCollection
        .find()
        .$.subscribe(() => {
          getSignAndStampUserLevelFromRxDB();
        });
      setSignAndStampUserLevelUpdateSubscription(signAndStampUserLevelUpdateSub);
    }
  };
  const onOpenUploadForSign = async () => {
    document.getElementById('selectImageForSign')?.click();
  };

  const onOpenUploadForStamp = async () => {
    document.getElementById('selectImageForStamp')?.click();
  };

  const onFilesSelected = async (images: ISignAndStampData[]) => {
    await setSelectedFiles(images);
  };

  const autoVersionIfFileExists = (name: string, index = 0): string => {
    let newName = name,
      ext = '';
    if (index) {
      if (newName.indexOf('.') > -1) {
        const tokens = newName.split('.');
        ext = '.' + tokens.pop();
        newName = tokens.join('.');
      }
      newName = `${newName} (${index})${ext}`;
    }
    const nameExists = selectedFiles.filter(f => f.updatedName === newName).length > 0;
    return nameExists ? autoVersionIfFileExists(name, index + 1) : newName;
  };

  const uploadStampImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file: File = e.target.files[0];
    if (!ALLOW_EXTENSIONS.exec(file.name)) {
      errorNotification(INVALID_FILE_TYPE);
      return;
    }
    if (hasSpecialCharacter(file.name)) {
      errorNotification(SPECIAL_CHARACTER_ERROR);
      return;
    }
    let validItem = null;
    validItem = await checkFileCorruption([file]);
    if (!validItem.length) {
      errorNotification(INVALID_FILE);
      return;
    }
    if (file) {
      const fileData = {
        fileData: file,
        updatedName: autoVersionIfFileExists(file.name),
        category: FileCategory.Stamp,
        isSignature: false,
      };
      setStampData(fileData);
      setSelectedFiles(arr => [...arr, ...[fileData]]);
    }
  };

  const uploadSignImage = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files) return;
    const file: File = e.target.files[0];
    if (!ALLOW_EXTENSIONS.exec(file.name)) {
      errorNotification(INVALID_FILE_TYPE);
      return;
    }
    if (hasSpecialCharacter(file.name)) {
      errorNotification(SPECIAL_CHARACTER_ERROR);
      return;
    }
    let validItem = null;
    validItem = await checkFileCorruption([file]);
    if (!validItem.length) {
      errorNotification(INVALID_FILE);
      return;
    }
    if (file) {
      const fileData = {
        fileData: file,
        updatedName: autoVersionIfFileExists(file.name),
        category: FileCategory.Sign,
        isSignature: false,
      };
      setSignData(fileData);
      setSelectedFiles(arr => [...arr, ...[fileData]]);
    }
  };

  const onsetDeleteUserFiles = async (data: ISignAndStampData[]) => {
    setDeleteUserFiles(data);
  };

  const handleFileDelete = async (fileId: string, fileCategory: number) => {
    switch (fileCategory) {
      case 11:
      case 12:
        await deleteSignAndStampFile(fileId.toString(), TRUE);
        onsetDeleteUserFiles(deleteUserFiles.filter(image => image.fileUUID !== fileId));
        break;
    }
  };

  const handleDelete = (file: ISignAndStampData) => {
    if (file.fileUUID) {
      if (userSignData && userSignData?.fileUUID === file.fileUUID) {
        if (isSignature) setIsSignature(false);
        setUserSignData(undefined);
      }
      if (userStampData && userStampData?.fileUUID === file.fileUUID) {
        setUserStampData(undefined);
      }
    } else {
      if (file.category === FileCategory.Sign) {
        if (isSignature) setIsSignature(false);
        setSignData(undefined);
      } else if (file.category === FileCategory.Stamp) {
        setStampData(undefined);
      }
    }

    if (deleteUserFiles) {
      deleteUserFiles.map(image => {
        if (image.fileUUID && file.fileUUID && image.fileUUID === file.fileUUID) {
          handleFileDelete(image.fileUUID, image.category);
        }
      });
    }

    onFilesSelected(selectedFiles.filter(image => image.updatedName !== file.updatedName));
  };

  const handleImageUploads = async (fileList: ISignAndStampData[]) => {
    const files = [...fileList];
    files.forEach((file, index) => {
      files[index].isSaveForFuture = true;
      files[index].isInspection = false;
    });
    if (deleteUserFiles) {
      files.forEach((file, index) => {
        deleteUserFiles.forEach((dFile, dIndex) => {
          if (files[index].updatedName === deleteUserFiles[dIndex].updatedName) {
            files[index].isSaveForFuture = false;
          }
        });
      });
    }

    try {
      await Promise.all(
        files.map(async (signAndStampData: ISignAndStampData) => {
          const fileData: File = signAndStampData.fileData as File;
          if (signAndStampData.isSaveForFuture) {
            await uploadSignAndStamp(
              `signAndStamp/${localStorageCurrentUserId}`,
              fileData,
              signAndStampData,
            );
          }
        }),
      );
    } catch (error) {
      // eslint-disable-next-line no-console
      console.log(
        '🚀 ~ file: SignatureAndStampUpload.tsx ~ line 302 ~ handleImageUploads ~ error',
        error,
      );
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(
        error,
        '~ file: SignatureAndStampUpload.tsx ~ line 302 ~ handleImageUploads ~',
      );
    }
    successNotification('Sign and Stamp Submitted Successfully');
  };

  const getSignature = async () => {
    const val = signatureCanvas?.toDataURL();
    const blob = await fetch(val as string).then(r => r.blob());
    const objectURL = URL.createObjectURL(blob);
    const fileName = 'sign.png';
    const file = new File([objectURL], `${fileName}`, { type: 'image/png' });
    const fileData = {
      fileData: file,
      updatedName: autoVersionIfFileExists(file.name),
      category: FileCategory.Sign,
      isSignature: true,
      isSaveForFuture: true,
      blob,
    };
    return fileData;
  };

  const handleSignAndStamp = async () => {
    if (isSignature) {
      const fileData = await getSignature();
      selectedFiles.push(fileData);
    }
    await handleImageUploads(selectedFiles);
  };

  const editImageFile = async (dataURL: string, fileName: string) => {
    const blobUrl = isJestTest() ? '' : await (await fetch(dataURL)).blob();
    const file = new File([blobUrl], `${fileName}`, { type: 'image/jpeg' });
    return file;
  };

  const getS3URL = async (fileId: string) => {
    const blobFile: Blob = (await getAttachmentFromIndexDB(fileId)) as Blob;
    const url = blobFile ? URL.createObjectURL(blobFile) : '';
    return url;
  };

  const setUrlToFile = async (data: ISignAndStampData) => {
    const url = await getS3URL(data.fileUUID as string);
    if (url !== '') {
      const file = await editImageFile(url, data.updatedName);
      const fileData: ISignAndStampData = {
        fileUUID: data.fileUUID,
        fileData: file as File,
        updatedName: data.updatedName,
        category: data.category,
        isSignature: false,
        isSaveForFuture: false,
      };
      setSelectedFiles(prevArr => {
        const filteredArr = prevArr.filter(item => item.fileUUID !== fileData.fileUUID);
        return [...filteredArr, ...[fileData]];
      });
      setDeleteUserFiles(prevArr => {
        const filteredArr = prevArr.filter(item => item.fileUUID !== fileData.fileUUID);
        return [...filteredArr, ...[fileData]];
      });
    }
  };

  const showUploadContainer = () => {
    setUploadContainer(true);
    setSignContainer(false);
    setIsSignature(false);
  };

  const showSignContainer = () => {
    setUploadContainer(false);
    setSignContainer(true);
  };

  useEffect(() => {
    if (userSignData || userStampData) {
      if (userSignData && userSignData.fileUUID) {
        setUrlToFile(userSignData);
      }
      if (userStampData && userStampData.fileUUID) {
        setUrlToFile(userStampData);
      }
    }
  }, [userSignData, userStampData]);

  useEffect(() => {
    if (signAndStampUserLevelInitialized) {
      getSignAndStampUserLevelFromRxDB();
      subscribeToSignAndStampUserLevel();
    }
    return () => {
      if (signAndStampUserLevelUpdateSubscription)
        signAndStampUserLevelUpdateSubscription.unsubscribe();
    };
  }, [signAndStampUserLevelInitialized]);

  useEffect(() => {
    //upload button
    if (signData || isSignature || stampData) {
      setIsUploadButDisable(false);
    } else if (!signData && !isSignature && !stampData) {
      setIsUploadButDisable(true);
    }
  }, [isSignature, signData, stampData]);

  useEffect(() => {
    if (isAccepted) setIsChecked(true);
  }, [isAccepted]);

  useEffect(() => {
    if (isHandleSignAndStamp) {
      handleSignAndStamp();
      setIsAccepted(false);
      setSaveSignAndStamp(false);
      setIsSignature(false);
      setIsUploadButDisable(true);
      setSelectedFiles([]);
    }
  }, [isHandleSignAndStamp]);

  return (
    <>
      <Card elevation={1} className={classes.card}>
        <Card elevation={1} className="grid">
          {!userSignData && !signData && (
            <>
              {signContainer && (
                <Grid item xs={12}>
                  <Grid item xs={4} className={classes.buttonGrid}>
                    <ButtonGroup>
                      <Button color="primary" variant="contained" className={classes.uploadButton}>
                        Digitally Sign
                      </Button>
                      <Button className="digitalButton" onClick={showUploadContainer}>
                        Upload Sign
                      </Button>
                    </ButtonGroup>
                  </Grid>
                  {isSignature && (
                    <Grid item xs={8} justifyContent="flex-end" className={classes.signStampTitle}>
                      <Typography
                        variant="h5"
                        color="error"
                        className="clearSignText"
                        onClick={() => {
                          signatureCanvas?.clear();
                          setIsSignature(false);
                        }}
                      >
                        {`Clear Sign`}
                      </Typography>
                    </Grid>
                  )}
                  <SignatureCanvas
                    ref={ref => {
                      signatureCanvas = ref;
                    }}
                    penColor={`${MaterialPalette.text.disabled}`}
                    backgroundColor="#404040"
                    canvasProps={{
                      height: signatureCanvasHeight,
                      className: classes.signCanvas,
                      style: { maxWidth: '100%', borderRadius: '4px' },
                    }}
                    onBegin={() => {}}
                    onEnd={() => setIsSignature(true)}
                  />
                </Grid>
              )}
              {uploadContainer && (
                <Grid>
                  <Grid xs={12} item container>
                    <Grid item xs={4} className={classes.buttonGrid}>
                      <ButtonGroup>
                        <Button className="digitalButton" onClick={showSignContainer}>
                          Digitally Sign
                        </Button>
                        <Button
                          color="primary"
                          variant="contained"
                          className={classes.uploadButton}
                        >
                          Upload Sign
                        </Button>
                      </ButtonGroup>
                    </Grid>
                  </Grid>
                  <Grid container justifyContent="center">
                    <Grid className={classes.stampLabelDiv}>
                      <Typography variant="body1">Select and Upload the Sign Image</Typography>
                    </Grid>
                    <Grid
                      container
                      justifyContent="center"
                      item
                      onClick={onOpenUploadForSign}
                      className={classes.uploadIconGrid}
                      data-testid="signUploadIcon"
                    >
                      <Icons.UploadFeather color="primary" height="40px" width="40px" />

                      <input
                        type="file"
                        accept="image/*"
                        hidden
                        id="selectImageForSign"
                        onClick={() => {}}
                        onChange={uploadSignImage}
                        data-testid="uploadSign"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              )}
            </>
          )}

          {userSignData && (
            <SignatureAndStampView
              data={userSignData}
              onDelete={handleDelete}
              readonly={false}
              isSign={true}
            />
          )}
          {signData && (
            <SignatureAndStampView
              data={signData}
              onDelete={handleDelete}
              readonly={false}
              isSign={true}
            />
          )}
        </Card>

        <Card elevation={1} className="card2">
          {!userStampData && !stampData && (
            <Grid container className={classes.stampUploadContainer}>
              <Grid container justifyContent="center">
                <Grid className={classes.stampLabelDiv}>
                  <Typography>Select and Upload the Stamping Image</Typography>
                </Grid>
                <Grid
                  container
                  justifyContent="center"
                  item
                  onClick={onOpenUploadForStamp}
                  className={classes.uploadIconGrid}
                  data-testid="stampUploadIcon"
                >
                  <Icons.UploadFeather color="primary" height="40px" width="40px" />
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    id="selectImageForStamp"
                    onClick={() => {}}
                    onChange={uploadStampImage}
                    data-testid="uploadStamp"
                  />
                </Grid>
              </Grid>
            </Grid>
          )}
          {userStampData && (
            <SignatureAndStampView
              data={userStampData}
              onDelete={handleDelete}
              readonly={false}
              isSign={false}
              isStamp={true}
            />
          )}
          {stampData && (
            <SignatureAndStampView
              data={stampData}
              onDelete={handleDelete}
              readonly={false}
              isSign={false}
              isStamp={true}
            />
          )}
        </Card>
        <Grid container className={classes.checkboxGrid}>
          <FormControlLabel
            control={
              <Checkbox
                color="primary"
                checked={isChecked}
                inputProps={{
                  'data-testid': 'save-for-future-checkbox',
                }}
              />
            }
            label={null}
          />
          <Typography className="checkboxLabel">{label}</Typography>
        </Grid>
        {openAlert && (
          <Grid className={classes.errorGrid}>
            <Icons.AlertCircleIcon color="error" />
            <Typography color="error" variant="body2">
              Complete reading User Consent
            </Typography>
          </Grid>
        )}
      </Card>

      {openUserConsent && (
        <UserConsent
          open={openUserConsent}
          onClose={() => setOpenUserConsent(false)}
          setIsAccepted={setIsAccepted}
          setIsChecked={setIsChecked}
          setIsOpenAlert={setIsOpenAlert}
        />
      )}
    </>
  );
};

export default SignatureAndStampUpload;
